import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type DiagnosticOverride = components['schemas']['DiagnosticOverrideResponse'];

/** The scopes `DiagnosticScope` declares. Account is the only one both personas hold. */
export const DiagnosticScopes = ['Account', 'Category', 'Global'] as const;
export type DiagnosticScopeName = (typeof DiagnosticScopes)[number];

/**
 * The levels this console offers.
 *
 * ⚠️ **Narrower than what the API accepts, deliberately.** `SetDiagnostics`
 * rejects only `None`, so `Information` through `Critical` would be accepted —
 * and would *reduce* a tenant's logging below the baseline. The tenant-facing
 * endpoint refuses anything above `Debug` for exactly that reason ("Diagnostics
 * may only be raised"); the staff endpoint does not, because staff legitimately
 * operate the whole mechanism.
 *
 * This story is "turn on verbose logging for one tenant", and a console that
 * makes it a two-click mistake to *silence* a tenant mid-incident is a different
 * and riskier tool. If muting is ever wanted it should arrive as its own control
 * with its own confirmation, not as two extra entries in this list.
 */
export const DiagnosticLevels = ['Debug', 'Trace'] as const;
export type DiagnosticLevelName = (typeof DiagnosticLevels)[number];

export const diagnosticsQueryKey = ['admin', 'diagnostics'] as const;

/**
 * Every active override, at every scope — the platform-wide view a tenant
 * deliberately cannot get (M0-33).
 *
 * `minutesRemaining` is computed by the server at response time, so it is
 * refetched rather than counted down in the browser. Counting down locally
 * would introduce clock skew between the browser and the server on a value
 * whose whole job is answering "when does this stop"; refetching keeps the
 * number the server's, at the cost of it being at most a minute old.
 *
 * Unlike the tenant reads, `GET /admin/diagnostics` writes **no audit row**
 * (`ListDiagnostics` records nothing), so polling it costs nothing anyone has
 * to read later — which is why this one query may do what M1-33's may not.
 */
export function useDiagnosticOverrides() {
  return useQuery({
    queryKey: diagnosticsQueryKey,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/admin/diagnostics');
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/admin/diagnostics failed: ${response.status}`);
      }
      return data;
    },
  });
}

/**
 * How the API refused, in the shape the dialog renders.
 *
 * The distinction matters because the two refusals arrive completely
 * differently. A **validation** failure is `application/problem+json` with an
 * `errors` map, and its messages are the API's own words — the TTL ceiling
 * names both the limit and what was asked for, which is what makes it
 * actionable. A **forbidden** is `TypedResults.Forbid()`: a bare 403 with no
 * body at all, so there is nothing to quote and the console has to supply the
 * sentence itself.
 */
export type DiagnosticRefusal =
  | { kind: 'forbidden' }
  | { kind: 'validation'; fieldErrors: Record<string, string[]> }
  | { kind: 'unknown'; status: number };

export class DiagnosticRefusalError extends Error {
  constructor(readonly refusal: DiagnosticRefusal) {
    super(`Diagnostics request refused: ${refusal.kind}`);
    this.name = 'DiagnosticRefusalError';
  }
}

export interface RaiseDiagnosticsInput {
  scope: DiagnosticScopeName;
  /** An account id for Account, a logger prefix for Category, absent for Global. */
  target: string | null;
  level: DiagnosticLevelName;
  /** Blank means "the platform default" — the server owns that number, not us. */
  hours: number | null;
  reason: string;
}

/**
 * Raise an override.
 *
 * ⚠️ **Nothing here pre-empts a refusal**, which is M1-34's explicit
 * instruction about the TTL ceiling: the client does not clamp `hours`, does
 * not know the ceiling, and does not check the persona before sending a scope.
 * A client that clamped would produce someone who believes diagnostics are on
 * for a week when the server would have said no — and a client that hid the
 * refusal behind its own validation would mean the server's rule is only ever
 * enforced somewhere nobody sees.
 */
export function useRaiseDiagnostics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RaiseDiagnosticsInput): Promise<DiagnosticOverride> => {
      const { data, error, response } = await apiClient.POST('/api/v1/admin/diagnostics', {
        body: {
          scope: input.scope,
          target: input.target,
          level: input.level,
          hours: input.hours,
          reason: input.reason,
        },
      });

      // 403 first, and by STATUS rather than by body: `TypedResults.Forbid()`
      // sends no body and no content-type, so `error` is undefined here and an
      // error-shaped check would fall through to the generic branch and lose
      // the one fact that matters.
      if (response.status === 403) {
        throw new DiagnosticRefusalError({ kind: 'forbidden' });
      }

      if (response.status === 400) {
        // Read off `error`, never `data` — openapi-fetch fills `data` only for
        // a 2xx, so a `data`-first read silently swallows the API's message and
        // leaves a generic "something went wrong" in its place (M1-05).
        const fieldErrors = (error as { errors?: Record<string, string[]> } | undefined)?.errors;
        throw new DiagnosticRefusalError(
          fieldErrors ? { kind: 'validation', fieldErrors } : { kind: 'unknown', status: 400 },
        );
      }

      if (!response.ok || !data) {
        throw new DiagnosticRefusalError({ kind: 'unknown', status: response.status });
      }

      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: diagnosticsQueryKey }),
  });
}

/** Clear one override. A 404 means someone else already did, or it expired. */
export function useClearDiagnostics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { response } = await apiClient.DELETE('/api/v1/admin/diagnostics/{id}', {
        params: { path: { id } },
      });

      if (response.status === 404) {
        throw new DiagnosticRefusalError({ kind: 'unknown', status: 404 });
      }
      if (!response.ok) {
        throw new DiagnosticRefusalError({ kind: 'unknown', status: response.status });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: diagnosticsQueryKey }),
  });
}

/**
 * The API's own messages for one field, or none.
 *
 * Keys are the server's (`Scope`, `Level`, `Request`) and are matched exactly.
 * Note that `Request` is where several distinct problems land — the TTL
 * ceiling, a missing reason, an account scope with no id — because they are all
 * thrown from inside `SetAsync` and caught as one. The console does **not**
 * try to re-route them to a field by reading the text: that would be
 * reimplementing the server's rules in the browser to guess at something the
 * server already said plainly.
 */
export function messagesFor(
  refusal: DiagnosticRefusal | undefined,
  field: 'Scope' | 'Level' | 'Request',
): string[] {
  return refusal?.kind === 'validation' ? (refusal.fieldErrors[field] ?? []) : [];
}

/** Any validation message the console did not place against a specific input. */
export function unplacedMessages(refusal: DiagnosticRefusal | undefined): string[] {
  if (refusal?.kind !== 'validation') {
    return [];
  }
  const placed = new Set(['Scope', 'Level']);
  return Object.entries(refusal.fieldErrors)
    .filter(([field]) => !placed.has(field))
    .flatMap(([, messages]) => messages);
}
